import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuth } from '@nyos/shared'
import {
  getInterchanges,
  getRoutableTracks,
  getAvailableLocomotives,
  getYardOccupancy,
  getAuditHistory,
  runDryRun,
  getTrackOccupancy,
  dispatchWorkOrder,
  getYardMap,
  getYardAssets,
  Interchange,
  Track,
  RailCar,
  ValidationError,
  DryRunResult,
  AuditEvent,
  PlannedCut,
  WorkOrderDispatchResponse,
  YardOccupancy,
  SwitchListDrawer,
  GisLayoutOrientation,
  YardMapFocusContext,
} from '@nyos/shared'
import { useToast } from '../../contexts/ToastContext'
import { useWebSocketContext } from '../../contexts/WebSocketContext'
import { useYard } from '../../contexts/YardContext'
import ScenarioControls from '../../components/ScenarioControls'
import SafetyAlertsPanel from '../../components/SafetyAlertsPanel'
import YardMapOverlayModal from '../../components/YardMapOverlayModal'
import ManifestTable from './components/ManifestTable'
import FootprintGrid from './components/FootprintGrid'
import PredictiveCapacityPanel from './components/PredictiveCapacityPanel'
import AuditTimelineStepper from './components/AuditTimelineStepper'
import DispatchGatewayModal from './components/DispatchGatewayModal'
import { resolveSourceTracks, resolveLocoSourceDefault } from './sourceTrackResolution'
import { ribbonHint } from './ribbonHint'
import { OctagonX, AlertTriangle } from 'lucide-react'

export default function InboundInterchange() {
  const { token, user } = useAuth()
  const { showToast } = useToast()
  const { subscribe, isConnected } = useWebSocketContext()
  const { activeFacilityId } = useYard()
  // ADR-001/007: X-Facility-ID selector wins; JWT facility only as fallback.
  const scopeFacilityId = activeFacilityId || user?.facilityId
  const [interchangeData, setInterchangeData] = useState<Interchange | null>(null)
  const [mapFocusContext, setMapFocusContext] = useState<YardMapFocusContext | null>(null)
  
  // Local spatial data for modal overlay (separate from main YardMap on "/" route)
  const [geoData, setGeoData] = useState<any>(null)
  const [yardAssets, setYardAssets] = useState<any[]>([])
  const [yardOrientation, setYardOrientation] = useState<GisLayoutOrientation>('NORMAL')
  const [interchangeQueue, setInterchangeQueue] = useState<Interchange[]>([])
  const [expandedManifestId, setExpandedManifestId] = useState<string | null>(null)
  const [cutIndex, setCutIndex] = useState<number | null>(null)
  const [isCutActive, setIsCutActive] = useState(false)
  const [availableTracks, setAvailableTracks] = useState<Track[]>([])
  const [availableLocomotives, setAvailableLocomotives] = useState<RailCar[]>([])
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const [selectedTrackId, setSelectedTrackId] = useState<string>('')
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null)
  const [selectedSourceTrackId, setSelectedSourceTrackId] = useState<string>('')
  // True while the source pick is the loco-track default, not a user choice.
  const [sourceDefaulted, setSourceDefaulted] = useState(false)
  const [selectedLocomotiveId, setSelectedLocomotiveId] = useState<string>('')
  const [selectedLocomotive, setSelectedLocomotive] = useState<RailCar | null>(null)
  const [processedAssetIds, setProcessedAssetIds] = useState<string[]>([])
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null)
  const [yardOccupancy, setYardOccupancy] = useState<YardOccupancy | null>(null)
  const [auditHistory, setAuditHistory] = useState<AuditEvent[]>([])
  const [isRunningDryRun, setIsRunningDryRun] = useState(false)
  const [isDispatchModalOpen, setIsDispatchModalOpen] = useState(false)
  const [isSwitchListDrawerOpen, setIsSwitchListDrawerOpen] = useState(false)
  const [lastDispatchResult, setLastDispatchResult] = useState<{ response: WorkOrderDispatchResponse; sourceTrackName: string; targetTrackName: string } | null>(null)
  const [plannedCuts, setPlannedCuts] = useState<PlannedCut[]>([])
  const [footprintGridKey, setFootprintGridKey] = useState(0)
  // Zone 3 foundation panel is tabbed (Alerts default) — full-width panels
  // stay legible on tablet-height viewports where side-by-side halves crushed
  // the audit stepper to zero visible rows.
  // TODO(tablet-refactor): the tab bar below (badge counts + auto-switch on
  // blocking severity + underline active state) is reusable — extract to a
  // `ZoneTabs` component (shared/src/components/ if other app targets need
  // it, desktop/src/components/ otherwise) when a second 3-zone page adopts
  // the same foundation-panel pattern.
  const [foundationTab, setFoundationTab] = useState<'alerts' | 'audit'>('alerts')

  // Zone 3 tab badges — same severity buckets SafetyAlertsPanel renders:
  // hard = ERROR/CRITICAL (red), soft = WARNING/INFO (amber). Unknown
  // severities fall into hard so nothing silently drops off the badge.
  const softAlertCount = validationErrors.filter(
    (e) => e.severity === 'WARNING' || e.severity === 'INFO'
  ).length
  const hardAlertCount = validationErrors.length - softAlertCount

  // Computed: pending rail cars not yet processed
  const pendingAssetIds = interchangeData?.railCars
    .filter(car => !processedAssetIds.includes(car.id))
    .map(car => car.id) || []

  // Option A: Marietta has no dedicated receiving lead — manifest cars
  // arrive with no current_track_id. Source list = manifest-tracked cars
  // when present, else the full routable set; the pick itself defaults
  // (labeled, editable) to the selected locomotive's current track, which
  // is what LOCOMOTIVE_NOT_ON_SOURCE_TRACK requires to pass.
  const manifestTrackIds = useMemo(() => new Set(
    (interchangeData?.railCars ?? [])
      .map(car => car.currentTrackId)
      .filter((id): id is string => !!id)
  ), [interchangeData])

  const { tracks: sourceTracks, mode: sourceTrackMode } = useMemo(
    () => resolveSourceTracks(availableTracks, manifestTrackIds),
    [availableTracks, manifestTrackIds])

  // Destination = full routable set minus the source — same contract as
  // the switch list builder's DestinationRail.
  const destinationTracks = useMemo(() =>
    availableTracks.filter(track => track.id !== selectedSourceTrackId),
    [availableTracks, selectedSourceTrackId])

  // Option A default: once a locomotive is picked and the user hasn't
  // chosen a source, preselect the track the locomotive stands on.
  // Non-silent — the dropdown shows it + a hint explains why.
  useEffect(() => {
    if (sourceTrackMode !== 'routable') {
      setSourceDefaulted(false)
      return
    }
    if (!selectedLocomotiveId) return
    // User-chosen source is never overridden; a stale default re-follows the loco.
    if (selectedSourceTrackId && !sourceDefaulted) return
    const track = resolveLocoSourceDefault(availableTracks, selectedLocomotiveId, availableLocomotives)
    if (track && track.id !== selectedSourceTrackId) {
      setSelectedSourceTrackId(track.id)
    }
    if (track) setSourceDefaulted(true)
  }, [sourceTrackMode, selectedLocomotiveId, selectedSourceTrackId, sourceDefaulted, availableTracks, availableLocomotives])

  // Close yard map overlay when switching interchanges
  useEffect(() => {
    setMapFocusContext(null)
  }, [interchangeData?.id])

  // Zone 3: pull focus to the Alerts tab when something actionable lands —
  // a blocking dry-run severity or newly arrived validation errors.
  const prevErrorCountRef = useRef(0)
  useEffect(() => {
    if (isBlockingSeverity(dryRunResult?.severity) || validationErrors.length > prevErrorCountRef.current) {
      setFoundationTab('alerts')
    }
    prevErrorCountRef.current = validationErrors.length
  }, [validationErrors.length, dryRunResult])

  // Fetch spatial data for modal overlay when yard changes
  useEffect(() => {
    const fetchSpatialData = async () => {
      // Selector scope wins (ADR-001): X-Facility-ID → JWT facility. The
      // manifest's destinationFacilityId is data, not scope — fallback only.
      const facilityId = scopeFacilityId || interchangeData?.destinationFacilityId
      
      if (!facilityId) {
        setGeoData(null)
        setYardAssets([])
        return
      }

      try {
        const [geo, assets] = await Promise.all([
          getYardMap(facilityId),
          getYardAssets(facilityId)
        ])
        setGeoData(geo)
        setYardAssets(assets)
        if (geo.metadata?.gisLayoutOrientation) {
          setYardOrientation(geo.metadata.gisLayoutOrientation as GisLayoutOrientation)
        }
      } catch (error) {
        console.error('[InboundInterchange] Failed to fetch spatial data for modal:', error)
      }
    }

    fetchSpatialData()
  }, [interchangeData?.destinationFacilityId, activeFacilityId])

  // Connect to WebSocket for real-time updates
  useEffect(() => {
    const handleMessage = (message: any) => {
      if (message.type === 'INTERCHANGE_INGESTED') {
        setInterchangeData({
          id: message.interchangeId,
          interchangeNumber: message.interchangeNumber,
          classICarrier: message.classICarrier,
          destinationFacilityId: message.destinationFacilityId,
          status: message.status,
          rawEdi: message.rawEdi || '',
          railCars: message.railCars || [],
          createdAt: new Date(message.timestamp).toISOString(),
          updatedAt: new Date(message.timestamp).toISOString()
        })
      } else if (message.type === 'KVE_CLEARANCE_VIOLATION') {
        setValidationErrors(prev => [...prev, {
          id: `violation-${Date.now()}`,
          severity: 'CRITICAL',
          message: message.message,
          trackId: message.trackId,
          timestamp: new Date(message.timestamp).toISOString()
        }])
      }
    }

    const unsubscribe = subscribe(handleMessage)
    return () => {
      unsubscribe()
    }
  }, [subscribe])

  const fetchTracks = useCallback(async (facilityId: string) => {
    console.log('fetchTracks called - fetching routable tracks for yard:', facilityId)
    try {
      const tracks = await getRoutableTracks(facilityId)
      console.log('fetchTracks response data:', tracks)
      setAvailableTracks(tracks)
    } catch (error) {
      console.error('Failed to fetch tracks:', error)
    }
  }, [])

  const fetchLocomotives = useCallback(async (facilityId: string) => {
    console.log('fetchLocomotives called - fetching available locomotives for yard:', facilityId)
    try {
      const locomotives = await getAvailableLocomotives(facilityId)
      console.log('fetchLocomotives response data:', locomotives)
      setAvailableLocomotives(locomotives)
    } catch (error) {
      console.error('Failed to fetch locomotives:', error)
    }
  }, [])

  // Fetch interchange queue
  const fetchInterchanges = useCallback(async () => {
    try {
      const interchanges = await getInterchanges()
      setInterchangeQueue(interchanges)

      // Data-Volume UI Initialization Rule:
      // If single manifest, expand by default. If multiple, collapse all.
      if (interchanges.length === 1) {
        setInterchangeData(interchanges[0])
        setExpandedManifestId(interchanges[0].id)
      } else if (interchanges.length > 1) {
        setInterchangeData(interchanges[0])
        setExpandedManifestId(null) // Start collapsed for multiple manifests
      }
    } catch (error) {
      console.error('Failed to fetch interchanges:', error)
    }
  }, [])

  // Fetch the interchange queue on mount and whenever the facility scope
  // changes — the queue is facility-scoped server-side via X-Facility-ID.
  useEffect(() => {
    fetchInterchanges()
  }, [fetchInterchanges, scopeFacilityId])

  // Scope switch: a draft selection belongs to the facility it was made
  // under — carrying ids across scopes would send foreign track/car ids
  // to the new facility. Reset all draft/selection state on scope change.
  const prevScopeRef = useRef(scopeFacilityId)
  useEffect(() => {
    if (prevScopeRef.current === scopeFacilityId) {
      return
    }
    prevScopeRef.current = scopeFacilityId
    setSelectedTrackId('')
    setSelectedTrack(null)
    setSelectedSourceTrackId('')
    setSourceDefaulted(false)
    setSelectedLocomotiveId('')
    setSelectedLocomotive(null)
    setProcessedAssetIds([])
    setPlannedCuts([])
    setCutIndex(null)
    setIsCutActive(false)
    setDryRunResult(null)
    setValidationErrors([])
    setInterchangeData(null)
    setExpandedManifestId(null)
    setMapFocusContext(null)
  }, [scopeFacilityId])

  // Fetch yard occupancy when yard is loaded
  const fetchYardOccupancy = useCallback(async (facilityId: string) => {
    try {
      const occupancy = await getYardOccupancy(facilityId)
      setYardOccupancy({
        totalPhysicalLength: occupancy.totalPhysicalLength,
        totalClearanceCapacity: occupancy.totalClearanceCapacity,
        totalOccupied: occupancy.totalOccupied,
        utilization: occupancy.utilization,
        totalCarCount: occupancy.totalCarCount,
        customerSpurOccupancy: occupancy.customerSpurOccupancy ?? null
      })
    } catch (error) {
      console.error('Failed to fetch yard occupancy:', error)
    }
  }, [])

  // Refresh upstream data when a work order status changes from the drawer
  const handleWorkOrderStatusChange = useCallback(async () => {
    const facilityId = scopeFacilityId
    if (!facilityId) return

    await Promise.all([
      fetchInterchanges(),
      fetchTracks(facilityId),
      fetchYardOccupancy(facilityId),
      fetchLocomotives(facilityId)
    ])

    setFootprintGridKey(prev => prev + 1)
  }, [scopeFacilityId, fetchInterchanges, fetchTracks, fetchYardOccupancy, fetchLocomotives])

  // Fetch tracks for user's assigned scope when user or token changes
  useEffect(() => {
    const facilityId = scopeFacilityId
    console.log('InboundInterchange: facilityId=', facilityId, 'token=', !!token, 'user=', user)
    if (facilityId && token) {
      fetchTracks(facilityId)
      fetchLocomotives(facilityId)
      fetchYardOccupancy(facilityId)
    }
  }, [scopeFacilityId, token, fetchTracks, fetchLocomotives, fetchYardOccupancy])

  // Fetch audit history when interchange changes
  useEffect(() => {
    const fetchAuditHistory = async () => {
      if (!interchangeData?.id || !token) {
        setAuditHistory([])
        return
      }

      try {
        const history = await getAuditHistory(interchangeData.id)
        setAuditHistory(history)
      } catch (error) {
        console.error('Failed to fetch audit history:', error)
      }
    }

    fetchAuditHistory()
  }, [interchangeData?.id, token])

  const executeDryRun = async (trackId?: string, locomotiveId?: string, cutIndexOverride?: number) => {
    const targetTrackId = trackId || selectedTrackId
    const targetLocomotiveId = locomotiveId || selectedLocomotiveId
    if (!interchangeData?.id || !targetTrackId || !token) {
      return
    }

    // Only execute dry-run if locomotive is selected
    if (!targetLocomotiveId) {
      console.warn('Dry-run skipped: No locomotive selected')
      return
    }

    setIsRunningDryRun(true)
    setDryRunResult(null)
    setValidationErrors([])
    setMapFocusContext(null)

    try {
      // If cut is active or cutIndexOverride is provided, only calculate footprint for First Pass block.
      // The cut index addresses the REMAINING cars — the manifest table only
      // renders unprocessed rows — so slice the filtered list, not the full
      // manifest (a pass-2+ cut would otherwise re-send processed cars).
      const effectiveCutIndex = cutIndexOverride !== undefined ? cutIndexOverride : cutIndex
      const remainingCars = interchangeData.railCars.filter(car => !processedAssetIds.includes(car.id))
      const railCarsToProcess = (isCutActive || cutIndexOverride !== undefined) && effectiveCutIndex !== null
        ? remainingCars.slice(0, effectiveCutIndex)
        : interchangeData.railCars

      const result = await runDryRun(interchangeData.id, {
        source_track_id: selectedSourceTrackId,
        target_track_id: targetTrackId,
        locomotive_id: targetLocomotiveId,
        processedAssetIds: processedAssetIds,
        rail_cars: railCarsToProcess
      })
      setDryRunResult(result)
      if (result.errors && result.errors.length > 0) {
        setValidationErrors(result.errors)
      }
      // Refresh track occupancy data after dry-run to get latest actual occupancy
      if (selectedTrackId) {
        try {
          const trackDetails = await getTrackOccupancy(selectedTrackId)
          setSelectedTrack(trackDetails)
        } catch (error) {
          console.error('Failed to refresh track occupancy after dry-run:', error)
        }
      }
    } catch (error) {
      console.error('Dry-run error:', error)
      setValidationErrors([{
        id: `error-${Date.now()}`,
        severity: 'ERROR',
        message: 'Failed to run the predictive assessment',
        timestamp: new Date().toISOString()
      }])
    } finally {
      setIsRunningDryRun(false)
    }
  }

  const handleTrackSelect = async (trackId: string) => {
    setSelectedTrackId(trackId)
    const track = availableTracks.find(t => t.id === trackId)
    setSelectedTrack(track || null)
    setDryRunResult(null)
    setValidationErrors([])
    
    // Fetch track details with occupancy data
    if (trackId) {
      try {
        const trackDetails = await getTrackOccupancy(trackId)
        setSelectedTrack(trackDetails)
      } catch (error) {
        console.error('Failed to fetch track details:', error)
      }
    }
  }

  const handleLocomotiveSelect = (locomotive: RailCar) => {
    setSelectedLocomotive(locomotive)
    setSelectedLocomotiveId(locomotive.id)
    // Auto-trigger dry-run when locomotive is selected (based on selected EDI message and track)
    if (interchangeData?.id && token && selectedTrackId) {
      executeDryRun(selectedTrackId, locomotive.id)
    }
  }

  const handleLocomotiveUnselect = () => {
    setSelectedLocomotive(null)
    setSelectedLocomotiveId('')
    setSourceDefaulted(false)
    // Clear dry-run results to disable button when locomotive is unselected
    setDryRunResult(null)
    setValidationErrors([])
  }

  const handleSetupComplete = () => {
    // Force FootprintGrid to remount to refresh locomotive dropdown
    setFootprintGridKey(prev => prev + 1)
  }

  const handleCleanupComplete = () => {
    // Force FootprintGrid to remount to refresh locomotive dropdown
    setFootprintGridKey(prev => prev + 1)
    // Reset locomotive selection state
    setSelectedLocomotiveId('')
    setSelectedLocomotive(null)
    setSourceDefaulted(false)
    // Clear dry-run results and validation errors to disable button
    setDryRunResult(null)
    setValidationErrors([])
  }

  const handleDryRun = async () => {
    executeDryRun()
  }

  const isBlockingSeverity = (severity?: string): boolean => {
    return severity === 'ERROR' || severity === 'CRITICAL';
  }

  const handleConfirmPass = async () => {
    if (!interchangeData) {
      return
    }

    let carsToProcess: any[]
    if (cutIndex !== null) {
      // Cut is active: process only the cut subset from REMAINING cars
      const remainingCars = interchangeData.railCars.filter(car => !processedAssetIds.includes(car.id))
      carsToProcess = remainingCars.slice(0, cutIndex)
    } else {
      // No cut: process all pending cars
      carsToProcess = interchangeData.railCars.filter(car => !processedAssetIds.includes(car.id))
    }

    const currentCutIds = carsToProcess.map(car => car.id)
    const updatedProcessedAssetIds = [...processedAssetIds, ...currentCutIds]
    setProcessedAssetIds(updatedProcessedAssetIds)

    // Build and append to plannedCuts
    const newCut: PlannedCut = {
      passNumber: plannedCuts.length + 1,
      trackId: selectedTrackId,
      locomotiveId: selectedLocomotiveId,
      carIds: currentCutIds
    }
    setPlannedCuts([...plannedCuts, newCut])
    // Reset cut state for next pass
    setCutIndex(null)
    setIsCutActive(false)
    setDryRunResult(null)
    setValidationErrors([])
    console.log('Confirmed pass. Processed asset IDs:', updatedProcessedAssetIds)

    // Predictive Capacity section is cleared; user must explicitly click "Run Predictive Path Assessment"
    // to see results for remaining cars
  }

  const handleResetPlan = () => {
    setProcessedAssetIds([])
    setCutIndex(null)
    setIsCutActive(false)
    setDryRunResult(null)
    setValidationErrors([])
    setPlannedCuts([])
    console.log('Reset plan. Cleared all processed asset IDs.')
  }

  const getFinalPlannedCuts = (): PlannedCut[] => {
    if (plannedCuts.length > 0) {
      // Multi-pass workflow: use manually confirmed cuts
      return plannedCuts
    }
    // Single-pass workflow: compute fallback on demand for display/dispatch
    return [{
      passNumber: 1,
      trackId: selectedTrackId,
      locomotiveId: selectedLocomotiveId,
      carIds: pendingAssetIds
    }]
  }

  const handleApprove = () => {
    // plannedCuts contains only manually confirmed cuts.
    // The single-pass fallback is computed on demand for display and dispatch.
    setIsDispatchModalOpen(true)
  }

  const handleDispatch = async (crewId: string) => {
    if (!selectedTrackId || !selectedLocomotiveId || !token) {
      return
    }

    try {
      const result = await dispatchWorkOrder({
        sourceTrackId: selectedSourceTrackId,
        targetTrackId: selectedTrackId,
        crewId: crewId,
        locomotiveId: selectedLocomotiveId,
        workflowContextId: interchangeData?.id,
        // Explicitly interchange-manifest dispatch (Option A discriminator).
        switchListMode: 'INTERCHANGE',
        plannedCuts: getFinalPlannedCuts()
      })
      console.log('Work order dispatched:', result)

      // Check if validation failed despite 200 OK response
      if (result.valid === false && result.errors) {
        const error = new Error('Validation failed')
        error.name = 'ValidationError'
        ;(error as any).validationErrors = result.errors
        throw error
      }

      if (result.id && result.switchList) {
        setLastDispatchResult({
          response: result,
          sourceTrackName: result.sourceTrackName ?? 'Source',
          targetTrackName: result.targetTrackName ?? 'Target',
        })
        setIsSwitchListDrawerOpen(true)
      }

        // Trigger success toast with context-aware message
        const manifestId = interchangeData?.id || interchangeData?.interchangeNumber || 'unknown'
        const message = `Work Order Dispatched: Assigned Crew ${result.crewId} to shift Manifest ${manifestId} to Track ${result.targetTrackId}. Spatial capacity soft-lock active.`
        showToast(message, 'success', 4000)

        // Refresh audit history
        if (interchangeData?.id) {
          const history = await getAuditHistory(interchangeData.id)
          setAuditHistory(history)
        }

        // Post-dispatch state management
        // Reset state
        setSelectedTrackId('')
        setSelectedSourceTrackId('')
        setSelectedLocomotiveId('')
        setSelectedLocomotive(null)
        setProcessedAssetIds([])
        setDryRunResult(null)
        setValidationErrors([])
        setPlannedCuts([])
        setSelectedTrack(null)
        setCutIndex(null)
        setIsCutActive(false)

        // Queue advance: remove dispatched manifest from queue
        if (interchangeData?.id) {
          setInterchangeQueue((prevQueue) => {
            const updatedQueue = prevQueue.filter((item) => item.id !== interchangeData.id)

            // Auto-select next manifest if queue has items
            if (updatedQueue.length > 0) {
              const nextManifest = updatedQueue[0]
              setInterchangeData(nextManifest)
              setExpandedManifestId(nextManifest.id)
            } else {
              // Queue is empty: clear selection and disable actions
              setInterchangeData(null)
              setExpandedManifestId(null)
            }

            return updatedQueue
          })
        }

        // Force FootprintGrid to remount to reset its internal state
        setFootprintGridKey(prev => prev + 1)

        // Refresh track topology/capacities data — active scope, not the
        // JWT facility (stale after a scope switch).
        if (scopeFacilityId) {
          fetchTracks(scopeFacilityId)
          fetchLocomotives(scopeFacilityId)
          fetchYardOccupancy(scopeFacilityId)
        }
    } catch (error) {
      console.error('Dispatch error:', error)
      showToast('Error dispatching work order', 'error', 4000)
      throw error
    }
  }

  const handleCutClick = (index: number) => {
    setCutIndex(index)
    setIsCutActive(true)
    // Re-run dry-run with First Pass block, passing cut index directly to avoid timing issues
    if (selectedTrackId) {
      executeDryRun(selectedTrackId, undefined, index)
    }
  }

  const handleManifestToggle = (manifestId: string) => {
    const isCurrentlyExpanded = expandedManifestId === manifestId
    const isDifferentManifest = interchangeData?.id !== manifestId
    setExpandedManifestId(isCurrentlyExpanded ? null : manifestId)
    const manifest = interchangeQueue.find(m => m.id === manifestId)
    if (manifest) {
      // Only update interchangeData if it's a different manifest
      if (isDifferentManifest) {
        setInterchangeData(manifest)
        setCutIndex(null)
        setIsCutActive(false)
        // Retain track and locomotive selection for quick re-validation
        // Only reset manifest-specific state
        setProcessedAssetIds([])
        setDryRunResult(null)
        setValidationErrors([])
      } else {
        setCutIndex(null)
        setIsCutActive(false)
      }
    }
  }

  // Ribbon disable-reason captions — mirror the disabled expressions below
  // so the FIRST blocking condition surfaces as helper text under the
  // button (tooltips don't exist on touch).
  const dryRunDisabled =
    !selectedTrackId || !selectedLocomotiveId || !interchangeData?.id ||
    isRunningDryRun || isCutActive || pendingAssetIds.length === 0
  const dryRunReason = !interchangeData?.id
    ? 'Select a manifest first'
    : pendingAssetIds.length === 0
      ? 'No pending cars to assess'
      : isCutActive
        ? 'Confirm or clear the active cut first'
        : !selectedTrackId
          ? 'Select a destination track'
          : !selectedLocomotiveId
            ? 'Select a locomotive'
            : isRunningDryRun
              ? 'Assessment in progress'
              : null

  const allProcessed = processedAssetIds.length > 0 && pendingAssetIds.length === 0
  const midPlan = processedAssetIds.length > 0 && pendingAssetIds.length > 0
  const assessmentOk = !!dryRunResult && !isBlockingSeverity(dryRunResult?.severity)
  const approveDisabled = !(assessmentOk || allProcessed) || isCutActive || midPlan
  const approveReason = midPlan
    ? 'Finish all passes before dispatching'
    : isCutActive
      ? 'Confirm or clear the active cut first'
      : !dryRunResult
        ? 'Run the predictive assessment first'
        : isBlockingSeverity(dryRunResult.severity) && !allProcessed
          ? 'Assessment has blocking findings'
          : null

  const confirmPassDisabled =
    (isCutActive && cutIndex === null) ||
    (!isCutActive && processedAssetIds.length === 0) ||
    pendingAssetIds.length === 0
  const confirmPassReason = pendingAssetIds.length === 0
    ? 'All cars are already placed'
    : !isCutActive && processedAssetIds.length === 0
      ? 'Place a cut in the manifest first'
      : isCutActive && cutIndex === null
        ? 'Choose where to cut'
        : null

  const resetDisabled = processedAssetIds.length === 0
  const resetReason = resetDisabled ? 'Nothing placed yet' : null

  // One ribbon hint line: the first unmet step, a positive state when the
  // next action is ready, or the blocking reason after a failed
  // assessment — a single line in a fixed slot, not per-button captions.
  const ribbon = ribbonHint({
    hasManifest: !!interchangeData?.id,
    pendingCount: pendingAssetIds.length,
    processedCount: processedAssetIds.length,
    isCutActive,
    cutIndex,
    hasDestinationTrack: !!selectedTrackId,
    hasLocomotive: !!selectedLocomotiveId,
    isRunningAssessment: isRunningDryRun,
    hasAssessment: !!dryRunResult,
    assessmentBlocked: !!dryRunResult && isBlockingSeverity(dryRunResult.severity),
    dispatched: isSwitchListDrawerOpen,
  })

  return (
    <div className="flex flex-col h-full gap-0 overflow-hidden">
      {/* Header with Validation Scenario Controls */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-main">
        <div className="flex flex-col">
          <span className="font-bold text-sm text-accent-blue whitespace-nowrap">Inbound Interchange</span>
          <span className="text-xs text-text-muted">Confirm every arriving car — placement, paperwork, and disputes caught at the gate</span>
        </div>
        <div className="flex items-center gap-2">
          <ScenarioControls
            workflowType="VALIDATION"
            onSetupComplete={handleSetupComplete}
            onCleanupComplete={handleCleanupComplete}
          />
        </div>
      </div>
      
      {/* ZONE 1: UPPER COCKPIT WORKSPACE (50% height — columns scroll internally) */}
      <div className="flex-1 flex flex-row gap-4 p-4 overflow-hidden" style={{ minHeight: '50%' }}>
        {/* Column 1: Inbound EDI Manifest Queue */}
        <div className="flex-1 border border-subtle rounded-lg p-4 bg-card overflow-hidden flex flex-col">
          <h3 className="font-bold mb-2 text-muted-foreground">Inbound EDI Manifest</h3>
          <div className="flex-1 overflow-y-auto">
            {interchangeQueue.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">No pending manifests</div>
            ) : (
              <div className="flex flex-col gap-2">
                {interchangeQueue.map((manifest) => {
                  const isExpanded = expandedManifestId === manifest.id
                  const isSelected = interchangeData?.id === manifest.id
                  const isProcessing = isCutActive || (processedAssetIds.length > 0 && pendingAssetIds.length > 0)
                  const isDisabled = !isSelected && isProcessing

                  return (
                    <div key={manifest.id} className={`border rounded-lg p-3 bg-background ${isSelected ? 'border-primary bg-primary/10' : 'border-subtle'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <div className="flex justify-between items-center mb-2 cursor-pointer" onClick={() => !isDisabled && handleManifestToggle(manifest.id)}>
                        <div>
                          <span className="font-medium">{manifest.interchangeNumber}</span>
                          <span className="ml-2 text-sm text-muted-foreground">{manifest.classICarrier}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isSelected && <span className="text-xs text-primary font-medium">● Active</span>}
                          <span className="text-muted-foreground hover:text-foreground transition-colors">
                            {isExpanded ? '▼' : '▶'}
                          </span>
                        </div>
                      </div>
                      {!isExpanded && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          {manifest.railCars.length} cars • {manifest.railCars.reduce((sum, car) => sum + (car.lengthFeet || 0), 0).toFixed(0)} ft
                        </div>
                      )}
                      {isExpanded && (
                        <ManifestTable
                          railCars={manifest.railCars.filter(car => !processedAssetIds.includes(car.id))}
                          cutIndex={cutIndex}
                          isCutActive={isCutActive}
                          onCutClick={handleCutClick}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Column 2: Physical Track Inventory Footprint */}
        <div className="flex-1 border border-subtle rounded-lg p-4 bg-card overflow-hidden flex flex-col">
          <h3 className="font-bold mb-2 text-muted-foreground">Physical Track Inventory</h3>
          {/* Single scroll region for the whole column body — Source
              Track select included, so nothing sits pinned above the
              scroll limit at tight heights. min-h-0 lets it shrink. */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="mb-3 flex items-center gap-3">
              <label className="text-sm font-semibold text-foreground">Source Track:</label>
              <select
                value={selectedSourceTrackId}
                onChange={(e) => { setSelectedSourceTrackId(e.target.value); setSourceDefaulted(false) }}
                className="px-3 py-1.5 border border-border rounded-md bg-background text-foreground text-sm flex-1"
              >
                <option value="">-- Select source track --</option>
                {sourceTracks.map(track => (
                  <option key={track.id} value={track.id}>{track.name}</option>
                ))}
              </select>
            </div>
            {sourceDefaulted && selectedSourceTrackId && (
              <div className="mb-3 -mt-2 text-xs text-muted-foreground">
                Source defaulted to the selected locomotive's current track — manifest cars carry no receiving track yet. Change it if the cut was delivered elsewhere.
              </div>
            )}
            <FootprintGrid
              key={footprintGridKey}
              availableTracks={destinationTracks}
              onTrackSelect={handleTrackSelect}
              onLocomotiveSelect={handleLocomotiveSelect}
              onLocomotiveUnselect={handleLocomotiveUnselect}
              facilityId={scopeFacilityId}
              selectedLocomotiveId={selectedLocomotiveId}
              selectedTrackId={selectedTrackId}
            />
          </div>
        </div>

        {/* Column 3: Global Yard Capacity & Predictive Utilization */}
        <div className="flex-1 border border-subtle rounded-lg p-4 bg-card overflow-hidden flex flex-col">
          <PredictiveCapacityPanel
            availableTracks={availableTracks}
            selectedTrack={selectedTrack}
            dryRunResult={dryRunResult}
            yardOccupancy={yardOccupancy}
            pendingAssetIds={pendingAssetIds}
            processedAssetIds={processedAssetIds}
          />
        </div>
      </div>

      {/* ZONE 2: THE COMMAND BRIDGE CONTROL RIBBON (10% height) */}
      <div className="h-[10%] border-t border-border bg-muted/50 flex items-center gap-4 px-4">
        <span
          id="ribbon-hint"
          role="status"
          className={`flex-1 min-w-0 truncate text-[11px] ${
            ribbon.tone === 'blocked'
              ? 'text-red-500 font-medium'
              : ribbon.tone === 'positive'
                ? 'text-green-600 dark:text-green-400 font-medium'
                : 'text-muted-foreground'
          }`}
        >
          {ribbon.text}
        </span>
        <button
          onClick={handleDryRun}
          disabled={dryRunDisabled}
          title={dryRunReason ?? 'Run the predictive path assessment'}
          aria-describedby="ribbon-hint"
          className="px-6 py-2 pointer-coarse:min-h-11 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-md font-medium transition-colors"
        >
          {isRunningDryRun ? 'Running Assessment...' : 'Run Predictive Path Assessment'}
        </button>
        <button
          onClick={handleApprove}
          disabled={approveDisabled}
          title={approveReason ?? 'Open the Dispatch Gateway'}
          aria-describedby="ribbon-hint"
          className="px-6 py-2 pointer-coarse:min-h-11 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-md font-medium transition-colors"
        >
          Dispatch Work Order…
        </button>
        <button
          onClick={handleConfirmPass}
          disabled={confirmPassDisabled}
          title={confirmPassReason ?? 'Confirm this pass and plan the next cut'}
          aria-describedby="ribbon-hint"
          className="px-6 py-2 pointer-coarse:min-h-11 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white rounded-md font-medium transition-colors"
        >
          Confirm Pass / Next Cut
        </button>
        <button
          onClick={handleResetPlan}
          disabled={resetDisabled}
          title={resetReason ?? 'Clear the placed passes'}
          aria-describedby="ribbon-hint"
          className="px-4 py-2 pointer-coarse:min-h-11 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white rounded-md font-medium transition-colors text-sm"
        >
          Reset Plan
        </button>
      </div>

      {/* ZONE 3: THE VALIDATION FOUNDATION PANEL (40% height) — tabbed:
          Alerts default with badge counts; auto-focuses on blocking results. */}
      <div className="h-[40%] min-h-[180px] flex flex-col border-t border-border overflow-hidden">
        <div className="flex items-center gap-1 px-4 border-b border-border bg-muted/30">
          <button
            onClick={() => setFoundationTab('alerts')}
            className={`px-3 py-1.5 pointer-coarse:min-h-11 -mb-px text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 ${
              foundationTab === 'alerts'
                ? 'border-accent-blue text-text-primary'
                : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            Safety Alerts
            {hardAlertCount > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-600 text-white">
                <OctagonX size={10} />
                {hardAlertCount}
              </span>
            )}
            {softAlertCount > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500 text-white">
                <AlertTriangle size={10} />
                {softAlertCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setFoundationTab('audit')}
            className={`px-3 py-1.5 pointer-coarse:min-h-11 -mb-px text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 ${
              foundationTab === 'audit'
                ? 'border-accent-blue text-text-primary'
                : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            Audit Trail
            {auditHistory.length > 0 && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-muted text-text-muted">
                {auditHistory.length}
              </span>
            )}
          </button>
        </div>
        <div className="flex-1 min-h-0 p-4 overflow-hidden flex flex-col">
          {foundationTab === 'alerts' ? (
            <SafetyAlertsPanel
              validationErrors={validationErrors}
              suggestedActions={dryRunResult?.suggestedActions}
              onViewOnMap={(ctx: YardMapFocusContext) => setMapFocusContext(ctx)}
              activeTheme="midnight"
            />
          ) : (
            <AuditTimelineStepper auditHistory={auditHistory} />
          )}
        </div>
      </div>

      {/* Dispatch Gateway Modal */}
      <DispatchGatewayModal
        isOpen={isDispatchModalOpen}
        onClose={() => setIsDispatchModalOpen(false)}
        facilityId={scopeFacilityId}
        sourceTrackId={selectedSourceTrackId}
        targetTrackId={selectedTrackId}
        selectedLocomotiveId={selectedLocomotiveId}
        plannedCuts={getFinalPlannedCuts()}
        onConfirm={handleDispatch}
        sourceTrackName={availableTracks.find(t => t.id === selectedSourceTrackId)?.name}
        targetTrackName={availableTracks.find(t => t.id === selectedTrackId)?.name}
        locomotiveReportingMark={selectedLocomotive?.reportingMark}
        availableTracks={availableTracks}
        availableLocomotives={availableLocomotives}
        railCars={interchangeData?.railCars}
      />

      {/* Switch List Drawer */}
      {lastDispatchResult && (
        <SwitchListDrawer
          isOpen={isSwitchListDrawerOpen}
          onClose={() => setIsSwitchListDrawerOpen(false)}
          workOrderId={lastDispatchResult.response.id ?? ''}
          switchList={lastDispatchResult.response.switchList!}
          woNumber={lastDispatchResult.response.woNumber ?? ''}
          swlNumber={lastDispatchResult.response.swlNumber ?? ''}
          sourceTrackName={lastDispatchResult.sourceTrackName}
          targetTrackName={lastDispatchResult.targetTrackName}
          isConnected={isConnected}
          subscribe={subscribe}
          onStatusChange={handleWorkOrderStatusChange}
        />
      )}

      {/* Yard Map Overlay Modal */}
      {mapFocusContext && (
        <YardMapOverlayModal
          focusContext={mapFocusContext}
          geoData={geoData}
          yardAssets={yardAssets}
          yardOrientation={yardOrientation}
          activeTheme="midnight"
          onClose={() => setMapFocusContext(null)}
        />
      )}

      {/* Loading Spinner Overlay */}
      {isRunningDryRun && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card border border-border p-6 rounded-lg shadow-lg flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            <p className="text-foreground font-medium">Running Predictive Path Assessment...</p>
          </div>
        </div>
      )}
    </div>
  )
}
